import React, { useState, useMemo } from "react";
import { rockIcons } from "../../Pages/Maps/MapConfig/rockIcons";


import { 
  pickaxeIcons, 
  sellPrices, 
  pickaxeRequirements,
  pickaxeDurability,
  pickaxeDropRates,
  LEVEL_REQUIREMENTS
} from "../../constants/items";

const MiningStoreModal = ({
  isOpen,
  toggle,
  miningMode,
  setMiningMode,
  /* remove old tab props */
  miningItems,
  miningInfo,
  loadingMiningInfo,
  currency,
  buyAmounts,
  sellAmounts,
  handleMiningQuantityChange,
  handleMiningPurchase,
  handleMiningSell,
  setMiningModeAndFetch
}) => {
  /* derive categories like department store */
  // derive player data
  const ownedPickaxeIds = useMemo(()=> (miningInfo?.pickaxes||[]).map(p=>p.pickaxe_id), [miningInfo]);
  const playerLevel = miningInfo?.mining_level || 0;
// helper to get level requirement for any item
const getLevelRequirement = (item) => {
  if(item.name?.toLowerCase().includes('pickaxe')) return pickaxeRequirements[item.id] || 0;
  return LEVEL_REQUIREMENTS[item.id] || item.levelRequired || 0;
};

// Helper to format gem names nicely
const formatGemName = (key) => {
  // Special handling for stone items
  if (key.includes('_stone')) {
    const parts = key.split('_');
    return parts[0].charAt(0).toUpperCase() + parts[0].slice(1) + ' Stone';
  } else if (key === 'metal_coat') {
    return 'Metal Coat';
  } else if (key === 'kings_rock') {
    return 'Kings Rock';
  }
  // Default handling for simple names
  return key.charAt(0).toUpperCase() + key.slice(1);
};

  const categories = useMemo(() => {
    const cats = new Set(['all','resources','sell']);
    (miningItems || []).forEach(it => {
      if(it.type){
        const key = String(it.type).toLowerCase();
        // skip purely numeric or empty keys
        if(key && isNaN(Number(key))) cats.add(key);
      } else if(it.name?.toLowerCase().includes('pickaxe')){cats.add('pickaxe');}
    });
    return Array.from(cats);
  }, [miningItems]);

  const [activeCategory, setActiveCategory] = useState('all');

  // Helper to build price display arrays
  const gemKeys = [
    'rock','ruby','sapphire','emerald','gold','diamond',
    'fire_stone','water_stone','thunder_stone','leaf_stone','moon_stone',
    'dawn_stone','metal_coat','kings_rock'
  ];

  // Mapping gem key -> backend item id
  const GEM_ITEM_ID = {
    sapphire: 222,
    ruby: 223,
    emerald: 224,
    gold: 225,
    diamond: 226,
    rock: 227,
    // Stone items
    fire_stone: 112,
    water_stone: 34,
    thunder_stone: 114,
    leaf_stone: 126,
    moon_stone: 22,
    dawn_stone: 26,
    metal_coat: 151,
    kings_rock: 135
  };

  const getImgSrc = (item) => {
    if(item.image) return `/${item.image}`;
    return `/items/${item.id}.png`;
  };

  const hasCost = (item) => {
    return (item.money||0)>0 || (item.coins||0)>0 || gemKeys.some(k=>(item[k]||0)>0);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="game-popup-overlay"
      onClick={toggle}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 10px',
        boxSizing: 'border-box',
        zIndex: 1050
      }}
    >
      <div 
        className="mining-store-popup"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '90%',
          maxWidth: 800,
          maxHeight: '90vh',
          background: 'rgba(29, 34, 46, 0.95)',
          color: '#fff',
          borderRadius: 12,
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>⛏️</span>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Mining Store</h3>
          </div>
          <button
            onClick={toggle}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              opacity: 0.7
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.7'}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: '20px',
          overflowY: 'auto',
          flex: 1
        }}>
        {loadingMiningInfo ? (
          <div style={{ textAlign: 'center', padding: '40px', fontSize: '16px', opacity: 0.8 }}>
            Loading mining store...
          </div>
        ) : miningMode === null ? (
          // Initial buy/sell choice screen
          <div style={{ textAlign: 'center' }}>
            <h4 style={{ margin: '0 0 24px', fontSize: '16px', fontWeight: 'normal', opacity: 0.9 }}>
              What would you like to do?
            </h4>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => setMiningModeAndFetch('buy')}
                style={{
                  background: 'rgba(74, 144, 226, 0.8)',
                  border: '1px solid rgba(74, 144, 226, 0.6)',
                  color: '#fff',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  minWidth: '160px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(74, 144, 226, 1)';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(74, 144, 226, 0.8)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <span>🛒</span>
                Buy Mining Items
              </button>
              <button
                onClick={() => setMiningModeAndFetch('sell')}
                style={{
                  background: 'rgba(40, 167, 69, 0.8)',
                  border: '1px solid rgba(40, 167, 69, 0.6)',
                  color: '#fff',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  minWidth: '160px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(40, 167, 69, 1)';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(40, 167, 69, 0.8)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <span>💰</span>
                Sell Mining Items
              </button>
            </div>
          </div>
        ) : miningMode === 'buy' ? (
          // Buy mode UI (category tabs)
          <div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={()=>setActiveCategory(cat)}
                  style={{
                    background: activeCategory === cat ? 'rgba(255, 193, 7, 0.8)' : 'rgba(108, 117, 125, 0.6)',
                    border: activeCategory === cat ? '1px solid rgba(255, 193, 7, 0.8)' : '1px solid rgba(108, 117, 125, 0.4)',
                    color: '#fff',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: activeCategory === cat ? 'bold' : 'normal',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (activeCategory !== cat) {
                      e.target.style.background = 'rgba(108, 117, 125, 0.8)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeCategory !== cat) {
                      e.target.style.background = 'rgba(108, 117, 125, 0.6)';
                    }
                  }}
                >
                  {cat.charAt(0).toUpperCase()+cat.slice(1)}
                </button>
              ))}
            </div>
            {activeCategory==='sell' ? (
              /* ---- Gem Selling Grid ---- */
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:'16px', marginBottom:'24px' }}>
                {gemKeys.map(k=>{
                  const ownedCount = miningInfo?.resources?.[k] ?? 0;
                  const itemId = GEM_ITEM_ID[k];
                  const quantity = sellAmounts[itemId] || 0;
                  const sellPrice = sellPrices[k] || 0;
                  const itemObj = { id:itemId, name:formatGemName(k), image:`Items/${k}.png` };
                  return (
                    <div key={k} style={{ background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:16, display:'flex', gap:12 }}>
                      <img src={rockIcons[k]} alt={k} style={{width:40,height:40,imageRendering:'pixelated'}} />
                      <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column'}}>
                        <h6 style={{margin:'0 0 4px',fontSize:14,fontWeight:'bold'}}>{itemObj.name}</h6>
                        <p style={{margin:'0 0 8px',fontSize:11,opacity:0.8}}><strong>Owned:</strong> {ownedCount}</p>
                        <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:8}}>
                          <span style={{fontSize:11,opacity:0.8}}>Sell Price:</span>
                          <img src="/items/coin.png" alt="Coin" style={{width:12,height:12}} />
                          <span style={{fontSize:11,color:'#28a745',fontWeight:'bold'}}>{sellPrice}</span>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <div style={{display:'flex',alignItems:'center',gap:4}}>
                            <label style={{fontSize:11,opacity:0.8}}>Qty:</label>
                            <input type="number" min="1" max={ownedCount} value={quantity} onChange={e=>{
                                const raw = Number(e.target.value);
                                const val = isNaN(raw)?1: Math.max(1, Math.min(ownedCount, raw));
                                handleMiningQuantityChange(itemId, val, false);
                              }} style={{width:50,padding:'2px 4px',background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)',borderRadius:4,color:'#fff',fontSize:11}} />
                          </div>
                          <button onClick={()=>handleMiningSell(itemObj)} disabled={quantity<=0 || ownedCount<=0} style={{background:(quantity<=0||ownedCount<=0)?'rgba(108,117,125,0.5)':'rgba(40,167,69,0.9)',border:'none',color:'#fff',padding:'8px 0',borderRadius:4,cursor:(quantity<=0||ownedCount<=0)?'not-allowed':'pointer',fontSize:11,fontWeight:'bold',opacity:(quantity<=0||ownedCount<=0)?0.6:1,width:'100%',textAlign:'center'}}>
                          Sell <img src="/items/coin.png" alt="Coin" style={{width:12,height:12,verticalAlign:'middle',marginRight:2}} /> {(sellPrice*quantity).toLocaleString()}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px', marginBottom: '20px' }}>
              {(miningItems||[])
                .filter(it=> {
                  if(activeCategory==='resources') return false; // resources handled separately

                  if(activeCategory==='pickaxe'){
                      if(!it.name?.toLowerCase().includes('pickaxe')) return false;
                    } else if(activeCategory!=='all'){
                      if((it.type||'misc')!==activeCategory) return false;
                    }
                  return hasCost(it);
                })
                .map(item=>{
                  const quantity=buyAmounts[item.id]||1;
                  const costMoney= (item.money||0)*quantity;
                  const costCoins= (item.coins||0)*quantity;
                  const gemCosts = gemKeys.reduce((acc,k)=>{acc[k]=(item[k]||0)*quantity; return acc;},{});
                  
            
                  
                  // Check if resources exist in the right place
                  const resources = miningInfo?.resources || {};
                  
                  // Check individual affordability for each resource
                  const moneyOk = (currency.money||0) >= costMoney;
                  const coinsOk = (currency.coins||0) >= costCoins;
  
                  
                  // Check each gem individually
                  // Direct check in canAfford below
                  
                  // IMPORTANT FIX: Use resources from miningInfo.resources directly
                  // The resources are stored in miningInfo.resources, not in currency
                  const canAfford = moneyOk && coinsOk && 
                    gemKeys.every(k => {
                      if(gemCosts[k] <= 0) return true;
                      
                      // Get the available amount from miningInfo.resources
                      const available = resources[k] || 0;
                      const isEnough = available >= gemCosts[k];
                      
             
                      return isEnough;
                    });
                  

                  const ownedPickaxe = item.name.toLowerCase().includes('pickaxe') && ownedPickaxeIds.includes(item.id);
                  const reqLevel = getLevelRequirement(item);
const levelReqOk = reqLevel === 0 || playerLevel >= reqLevel;
                  const disableBtn = ownedPickaxe || !levelReqOk || !canAfford;

                  return (
                    <div key={item.id} style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      padding: '12px',
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'flex-start'
                    }}>
                      <div style={{ flexShrink: 0 }}>
                        <img src={getImgSrc(item)} alt={item.name} style={{width:40,height:40,imageRendering:'pixelated'}} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h6 style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>{item.name}</h6>
                        <p style={{ margin: '0 0 8px', fontSize: '11px', opacity: 0.8, lineHeight: '1.3' }}>{item.desc||item.description||'No description.'}</p>
                        {/* Show level requirement & durability for pickaxes; show level requirement for other equipment */}
{(item.name.toLowerCase().includes('pickaxe') || LEVEL_REQUIREMENTS[item.id]) && (
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span style={{ fontSize: '11px', color: '#ffc107' }}><strong>Lvl Req:</strong> {getLevelRequirement(item)}</span>
                              {(pickaxeDurability[item.id]||0) > 0 && (
  <span style={{ fontSize: '11px', color: '#ffc107' }}><strong>Durability:</strong> {pickaxeDurability[item.id]}</span>
)}
                              {item.id === 121 && (
  <span style={{ fontSize: '11px', color: '#ffc107', fontWeight: 'bold' }}><strong>Max Stack:</strong> 10</span>
)}
                            </div>
                            {item.name.toLowerCase().includes('pickaxe') && pickaxeDropRates[item.id] && (
                              <div style={{ fontSize: '10px', color: '#ccc', lineHeight: '1.2' }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Drop Rates:</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px' }}>
                                  <div>Ruby: {(pickaxeDropRates[item.id].ruby * 100).toFixed(1)}%</div>
                                  <div>Sapphire: {(pickaxeDropRates[item.id].sapphire * 100).toFixed(1)}%</div>
                                  <div>Emerald: {(pickaxeDropRates[item.id].emerald * 100).toFixed(1)}%</div>
                                  <div>Gold: {(pickaxeDropRates[item.id].gold * 100).toFixed(1)}%</div>
                                  {pickaxeDropRates[item.id].diamond > 0 && (
                                    <div>Diamond: {(pickaxeDropRates[item.id].diamond * 100).toFixed(1)}%</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                          {costMoney>0 && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', background: 'rgba(40, 167, 69, 0.2)', padding: '2px 6px', borderRadius: '4px' }}>
                              <img src="/items/money.png" alt="Money" style={{ width: 12, height: 12 }} />
                              ${costMoney.toLocaleString()}
                            </span>
                          )}
                          {costCoins>0 && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', background: 'rgba(255, 193, 7, 0.2)', padding: '2px 6px', borderRadius: '4px' }}>
                              <img src="/items/coin.png" alt="Coins" style={{ width: 12, height: 12 }} />
                              {costCoins.toLocaleString()}
                            </span>
                          )}
                          {gemKeys.map(k=> gemCosts[k]>0 && (
                            <span key={k} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', background: 'rgba(156, 39, 176, 0.2)', padding: '2px 6px', borderRadius: '4px' }}>
                              <img src={rockIcons[k] || `/items/${k}.png`} alt={k} style={{ width: 12, height: 12 }} />
                              {gemCosts[k].toLocaleString()}
                            </span>
                          ))}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {item.type!=='pickaxe' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <label style={{ fontSize: '11px', opacity: 0.8 }}>Qty:</label>
                              <input 
                                type="number" 
                                min="1" 
                                max="99" 
                                value={quantity} 
                                onChange={(e)=>handleMiningQuantityChange(item.id,e.target.value)}
                                style={{
                                  width: '50px',
                                  padding: '2px 4px',
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                  borderRadius: '4px',
                                  color: '#fff',
                                  fontSize: '11px'
                                }}
                              />
                            </div>
                          )}
                          <button 
                            onClick={()=>handleMiningPurchase(item)} 
                            disabled={disableBtn}
                            style={{
                              background: disableBtn ? 'rgba(108, 117, 125, 0.5)' : 'rgba(74, 144, 226, 0.8)',
                              border: 'none',
                              color: '#fff',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: disableBtn ? 'not-allowed' : 'pointer',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              opacity: disableBtn ? 0.6 : 1
                            }}
                          >
                            {ownedPickaxe ? 'Owned' : !levelReqOk ? `Lvl ${getLevelRequirement(item)}` : !canAfford ? "Can't Afford" : 'Buy'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
                        )}

            {/* Resources view */}
            {activeCategory==='resources' && (
              <div>
                <h5 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 'bold', color: '#fff' }}>Your Resources</h5>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                  <div style={{ background: 'rgba(40, 167, 69, 0.2)', border: '1px solid rgba(40, 167, 69, 0.3)', borderRadius: '8px', padding: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img src="/items/money.png" alt="Money" style={{ width: 24, height: 24 }} />
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>${(currency.money||0).toLocaleString()}</span>
                  </div>
                  <div style={{ background: 'rgba(255, 193, 7, 0.2)', border: '1px solid rgba(255, 193, 7, 0.3)', borderRadius: '8px', padding: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img src="/items/coin.png" alt="Coins" style={{ width: 24, height: 24 }} />
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{(currency.coins||0).toLocaleString()} Coins</span>
                  </div>
                  {gemKeys.map(k=> (
                    <div key={k} style={{ background: 'rgba(156, 39, 176, 0.2)', border: '1px solid rgba(156, 39, 176, 0.3)', borderRadius: '8px', padding: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <img src={rockIcons[k] || `/items/${k}.png`} alt={k} style={{ width: 24, height: 24 }} />
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{(miningInfo?.resources?.[k]||0).toLocaleString()} {formatGemName(k)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
           </div>
        ) : (
          // Sell mode UI (legacy, keep non-gem items)
          <div>
            <h4 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 'bold', color: '#fff' }}>Sell Gems</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              {gemKeys.map(k=>{
                const ownedCount = miningInfo?.resources?.[k] ?? 0;
                const itemId = GEM_ITEM_ID[k];
                const quantity = sellAmounts[itemId] || 0;
                const sellPrice = sellPrices[k] || 0;
                // synthetic item for handler
                const itemObj = { id: itemId, name: formatGemName(k) };
                return (
                  <div key={k} style={{ background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:16, display:'flex', gap:12 }}>
                    <img src={rockIcons[k]} alt={k} style={{width:40,height:40,imageRendering:'pixelated'}} />
                    <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column'}}>
                      <h6 style={{margin:'0 0 4px',fontSize:14,fontWeight:'bold'}}>{itemObj.name}</h6>
                      <p style={{margin:'0 0 8px',fontSize:11,opacity:0.8}}><strong>Owned:</strong> {ownedCount}</p>
                      <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:8}}>
                        <span style={{fontSize:11,opacity:0.8}}>Sell Price:</span>
                        <img src="/items/coin.png" alt="Coin" style={{width:12,height:12}} />
                        <span style={{fontSize:11,color:'#28a745',fontWeight:'bold'}}>{sellPrice}</span>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <div style={{display:'flex',alignItems:'center',gap:4}}>
                          <label style={{fontSize:11,opacity:0.8}}>Qty:</label>
                          <input type="number" min="1" max={ownedCount} value={quantity} onChange={e=>{
                                const raw = Number(e.target.value);
                                const val = isNaN(raw)?1: Math.max(1, Math.min(ownedCount, raw));
                                handleMiningQuantityChange(itemId, val, false);
                              }} style={{width:50,padding:'2px 4px',background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)',borderRadius:4,color:'#fff',fontSize:11}} />
                        </div>
                        <button onClick={()=>handleMiningSell(itemObj)} disabled={quantity<=0 || ownedCount<=0} style={{background:(quantity<=0||ownedCount<=0)?'rgba(108,117,125,0.5)':'rgba(40,167,69,0.9)',border:'none',color:'#fff',padding:'8px 0',borderRadius:4,cursor:(quantity<=0||ownedCount<=0)?'not-allowed':'pointer',fontSize:11,fontWeight:'bold',opacity:(quantity<=0||ownedCount<=0)?0.6:1,width:'100%',textAlign:'center'}}>
                          Sell <img src="/items/coin.png" alt="Coin" style={{width:12,height:12,verticalAlign:'middle',marginRight:2}} /> {(sellPrice*quantity).toLocaleString()}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Sell Mining Items (non-gem) */}
            <h4 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 'bold', color: '#fff' }}>Sell Mining Items</h4>
            {miningItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', fontSize: '14px', opacity: 0.8 }}>No items to sell.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                {miningItems
                  .filter(item => {
                    // Only show items the player owns
                    const ownedCount = miningInfo?.inventory?.[item.id] || 0;
                    return ownedCount > 0;
                  })
                  .map(item => {
                    const ownedCount = miningInfo?.inventory?.[item.id] || 0;
                    const sellPrice = sellPrices[item.id] || 0;
                    const quantity = sellAmounts[item.id] || 1;
                    
                    return (
                      <div key={item.id} style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'flex-start'
                      }}>
                        <div style={{ flexShrink: 0 }}>
                          <img 
                            src={item.type === 'pickaxe' ? (pickaxeIcons[item.id] || `/items/${item.id}.png`) : `/items/${item.id}.png`} 
                            alt={item.name} 
                            style={{width:40,height:40,imageRendering:'pixelated'}}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h6 style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>{item.name}</h6>
                          <p style={{ margin: '0 0 8px', fontSize: '11px', opacity: 0.8 }}><strong>Owned:</strong> {ownedCount}</p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                            <span style={{ fontSize: '11px', opacity: 0.8 }}>Sell Price:</span>
                            <img src="/items/coin.png" alt="Coin" style={{ width: 12, height: 12 }} />
                            <span style={{ fontSize: '11px', color: '#28a745', fontWeight: 'bold' }}>{sellPrice}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <label style={{ fontSize: '11px', opacity: 0.8 }}>Qty:</label>
                              <input 
                                type="number" 
                                min="1" 
                                max={ownedCount} 
                                value={quantity} 
                                onChange={(e) => handleMiningQuantityChange(item.id, e.target.value, false)}
                                style={{
                                  width: '50px',
                                  padding: '2px 4px',
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                  borderRadius: '4px',
                                  color: '#fff',
                                  fontSize: '11px'
                                }}
                              />
                            </div>
                            <button
                              onClick={() => handleMiningSell(item)}
                              disabled={ownedCount <= 0}
                              style={{
                                background: ownedCount <= 0 ? 'rgba(108, 117, 125, 0.5)' : 'rgba(40, 167, 69, 0.9)',
                                border: 'none',
                                color: '#fff',
                                padding: '8px 0',
                                borderRadius: '4px',
                                cursor: ownedCount <= 0 ? 'not-allowed' : 'pointer',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                width: '100%',
                                textAlign: 'center',
                                opacity: ownedCount <= 0 ? 0.6 : 1
                              }}
                            >
                              Sell <img src="/items/coin.png" alt="Coin" style={{width:12,height:12,verticalAlign:'middle',marginRight:2}} /> {(sellPrice * quantity).toLocaleString()}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default MiningStoreModal;
